import styles from "./InstrumentOverviewSection.module.scss";
import { useRef, useState, useCallback } from "react";

import React from "react";
import {
  IInstrumentInsight,
  useInstrumentInsight,
} from "@iliotech/watchlist-api";
import { useClickAway } from "react-use";
import MarketInsightSingleItemTable from "../MarketInsightSingleItemTable/MarketInsightSingleItemTable";
import { GoodToKnowIcon } from "../../../GoodToKnowBlock/GoodToKnowIcon";
interface IOwnProps {
  Component: React.FC<any>;
  instrumentId: number;
  insightId: string;
  title: string;
  children?: React.ReactNode;
  sortedItems?: any[];
  collapsible?: boolean;
  overrideGoodToKnow?: (data: IInstrumentInsight<any>) => string | null;
  titleClassName?: string;
  transformer?: (data: any) => any;
  hideTable?: boolean;
  hideStats?: boolean;
  watchlistId?: string;
}

const ENVIRONMENT = process.env.NEXT_PUBLIC_ENVIRONMENT || "dev";

export const InstrumentOverviewSection = ({
  Component,
  instrumentId,
  insightId,
  title,
  children,
  sortedItems,
  collapsible = true,
  overrideGoodToKnow,
  titleClassName,
  transformer,
  hideTable = false,
  hideStats = false,
  watchlistId,
}: IOwnProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const tooltipVisible = useRef(false);
  const [ready, setReady] = React.useState(false);

  const { data, error, isLoading } = useInstrumentInsight(
    instrumentId,
    insightId,
    watchlistId
  );
  //@ts-ignore
  const tableData = data?.chart?.data?.tableData || [];

  const isCrypto = data?.instrument?.type === "Crypto-Currency";
  // Move shouldDisplayData to a memoized value
  const shouldDisplayData = React.useMemo(() => {
    if (title === "1 yr Risk Return") {
      const actualData: any = data?.chart?.data;
      const riskReturnChartData = actualData?.riskReturnChartData;
      return riskReturnChartData?.data.timeSeries.length > 1;
    }
    if (insightId === "INSTRUMENT_SUMMARY_TECHNICALS") {
      const chartData: any = data?.chart?.data;
      const chartsData: any = chartData?.chartsData;
      return chartsData?.length > 0;
    }
    return true; // Default to showing data for other insight types
  }, [data, insightId]);

  const anchor = useRef(null);

  const toggleTooltip = useCallback((e: React.MouseEvent) => {
    tooltipVisible.current = !tooltipVisible.current;
    //@ts-ignore
    const tooltipElement = anchor.current?.querySelector(`.${styles.tooltip}`);
    if (tooltipElement) {
      tooltipElement.style.display = tooltipVisible.current ? "block" : "none";
    }
  }, []);

  useClickAway(anchor, () => {
    if (tooltipVisible.current) {
      tooltipVisible.current = false;
      //@ts-ignore
      const tooltipElement = anchor.current?.querySelector(
        `.${styles.tooltip}`
      );
      if (tooltipElement) {
        tooltipElement.style.display = "none";
      }
    }
  });

  const goodToKnow = data?.insight?.whyImportant;
  React.useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) return null;
  if (isLoading) return <div>Loading...</div>;
  if (error)
    return (
      <div>
        <b>
          Sorry - we&apos;re not able to display the {insightId} insight right
          in {ENVIRONMENT} at the moment
        </b>
      </div>
    );

  if (!shouldDisplayData) return null;

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        <h2
          className={`${styles.collapsibleTitle} ${titleClassName}`}
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {title}
          {collapsible && (
            <span
              className={`${styles.arrow} ${
                isCollapsed ? styles.collapsed : ""
              }`}
            >
              ▼
            </span>
          )}
        </h2>
        <div
          ref={anchor}
          className={styles.goodToKnowButton}
          onClick={toggleTooltip}
        >
          <GoodToKnowIcon size={18} />
          Why is this good to know?
          <div className={styles.tooltip} style={{ display: "none" }}>
            {overrideGoodToKnow ? overrideGoodToKnow(data!) : goodToKnow}
          </div>
        </div>
      </div>
      {!isCollapsed && (
        <div className={styles.content}>
          <div
            style={{
              borderRadius: 20,
              margin: "40px 0",
              padding: 10,
            }}
          >
            <Component
              showStats={!hideStats}
              showHeader={false}
              showGoodToKnow={false}
              instrument={data?.instrument}
              insight={data?.insight}
              chart={data?.chart}
              transformer={transformer}
              onSeriesClick={() => {}}
            />
          </div>
          {!hideTable && tableData.length > 0 && (
            <MarketInsightSingleItemTable
              sortedItems={sortedItems!}
              category={insightId}
              data={tableData[0]}
            />
          )}
          {children}
        </div>
      )}
    </div>
  );
};
